import React from 'react';
import { Popover, Card, Button, Form, Input, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';

const cache: CellMeasurerCache = new CellMeasurerCache({ defaultHeight: 30, fixedWidth: true });

type TableFilterPopoverStringProps = {
  children: React.ReactNode;
  values: string[];
  initiallySelectedValues: string[];
  handleChange: (selectedValues: string[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  showSearch?: boolean;
  height?: number;
};

const TableFilterPopoverString: React.FC<TableFilterPopoverStringProps> = props => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const [keywords, setKeywords] = React.useState('');

  const formMethods = useForm({
    defaultValues: {
      selectedValues: props.initiallySelectedValues,
    },
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, watch, formState, reset } = formMethods;
  const selectedValues = watch('selectedValues');

  React.useEffect(() => {
    reset({ selectedValues: props.initiallySelectedValues });
  }, [props.initiallySelectedValues, reset]);

  React.useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('brandSearch');
      if (popoverVisible && input) input.focus();
    }, 100);
  }, [popoverVisible]);

  React.useEffect(() => {
    if (popoverVisible) handleResize();
  }, [popoverVisible]);

  const handleVisibleChange = (visible: boolean) => {
    setKeywords('');
    setPopoverVisible(visible);
    if (visible && props.onOpen) props.onOpen();
    else if (props.onClose) props.onClose();
  };

  const handleResize = () => {
    cache.clearAll();
  };

  const data = props.values.filter(value => value.toLowerCase().includes(keywords.toLowerCase()));

  const onSubmit = (values: { selectedValues: string[] }) => {
    props.handleChange(values.selectedValues);
    handleVisibleChange(false);
  };

  const renderRow = ({ index, parent, key, style }: any) => {
    const row = data[index];
    const selected = selectedValues.includes(row);

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div
          key={key}
          style={style}
          className="row table-filter-popover__search-table_row flex"
          onClick={() =>
            setValue(
              'selectedValues',
              selected
                ? selectedValues.filter(val => val !== row)
                : [...selectedValues, row].sort(),
              {
                shouldDirty: true,
              }
            )
          }
        >
          <div>
            <Checkbox checked={selected} />
          </div>
          <div className="flex-1 pl-3">{row}</div>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      overlayClassName="table-filter-popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        <Card>
          <FormProvider {...formMethods}>
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
              <div className="table-popover-search">
                {props.showSearch && (
                  <Input.Search
                    id="brandSearch"
                    value={keywords}
                    onChange={e => {
                      setKeywords(e.target.value);
                      handleResize();
                    }}
                  />
                )}
              </div>

              <div className="table-filter-popover__search-table">
                <List
                  deferredMeasurementCache={cache}
                  height={props.height || 300}
                  rowCount={data.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={renderRow}
                  width={300}
                />
              </div>

              <div className="flex-col items-end mt-3 mr-3">
                <div>
                  <Button
                    onClick={() => {
                      setValue('selectedValues', []);
                      handleSubmit(onSubmit)();
                    }}
                    className="drawer-submit__bottom-cancel"
                  >
                    {t('common:reset')}
                  </Button>
                  <Button type="primary" htmlType="submit" disabled={!formState.isDirty}>
                    {t('common:ok')}
                  </Button>
                </div>
              </div>
            </Form>
          </FormProvider>
        </Card>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default TableFilterPopoverString;
